/**
 * Entries component - manages the list of wheel entries
 */

import React, { useState, useCallback } from 'react';
import { makeId } from '../utils/helpers';
import type { SpinnerWheelEntriesProps, WheelEntry } from '../types/wheel';
import './SpinnerWheelEntries.css';

export const SpinnerWheelEntries: React.FC<SpinnerWheelEntriesProps> = ({
  entries,
  winner,
  isSpinning,
  variant = 'white',
  maxEntryLength = 2500,
  onAdd,
  onRemove,
  className = '',
}) => {
  const [newEntry, setNewEntry] = useState('');

  const remainingChars = maxEntryLength - newEntry.length;

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let value = e.target.value;
      if (value.length > maxEntryLength) {
        value = value.substring(0, maxEntryLength);
      }
      setNewEntry(value);
    },
    [maxEntryLength]
  );

  const handleSubmit = useCallback(() => {
    if (!newEntry.trim() || isSpinning) return;

    const newEntries: WheelEntry[] = newEntry
      .split(',')
      .map((name) => name.trim())
      .filter((name) => name.length > 0)
      .map((name) => ({
        id: makeId(),
        name,
      }));

    if (newEntries.length > 0) {
      onAdd(newEntries);
    }

    setNewEntry('');
  }, [newEntry, isSpinning, onAdd]);

  const handleKeyUp = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  const handleRemove = useCallback(
    (index: number) => {
      if (isSpinning) return;
      onRemove(index);
    },
    [isSpinning, onRemove]
  );

  const addRandomEntries = useCallback(
    (count: number) => {
      if (isSpinning) return;

      const adjectives = [
        'Swift', 'Brave', 'Lucky', 'Silent', 'Mighty', 'Wild', 'Cosmic', 'Iron',
        'Hidden', 'Shiny', 'Crimson', 'Golden', 'Frosty', 'Rapid', 'Calm',
        'Eager', 'Fuzzy', 'Bold', 'Quiet', 'Jolly',
      ];
      const nouns = [
        'Tiger', 'Falcon', 'Otter', 'Comet', 'Panda', 'Wolf', 'Dragon', 'Robot',
        'Phoenix', 'Knight', 'Ninja', 'Pirate', 'Wizard', 'Captain', 'Ranger',
        'Hunter', 'Scholar', 'Pilot', 'Sailor', 'Bard',
      ];
      const start = entries.length + 1;
      const newEntries: WheelEntry[] = new Array(count);
      for (let i = 0; i < count; i++) {
        const adj = adjectives[Math.floor(Math.random() * adjectives.length)];
        const noun = nouns[Math.floor(Math.random() * nouns.length)];
        newEntries[i] = {
          id: makeId(),
          name: `${adj} ${noun} ${start + i}`,
        };
      }
      onAdd(newEntries);
    },
    [isSpinning, entries.length, onAdd]
  );

  return (
    <div className={`entries --${variant} ${className}`}>
      {/* Header with entry count */}
      <div className="entries__header">
        <span className="entries__title">Entries</span>
        <span className="entries__count">{entries.length}</span>
      </div>

      {/* Input for new entries */}
      <div className="entries__input-wrapper">
        <div className="entries__input">
          <div className="entries__input-container">
            <input
              type="text"
              className="entries__input-field"
              placeholder="entry1, entry2, ..."
              value={newEntry}
              disabled={isSpinning}
              onChange={handleInputChange}
              onKeyUp={handleKeyUp}
            />
            <span className="entries__input-limit">{remainingChars}</span>
          </div>
          <button
            className="entries__add-btn"
            disabled={isSpinning || !newEntry.trim()}
            onClick={handleSubmit}
          >
            Add
          </button>
        </div>
        <p className="entries__input-hint">Enter entries separated by a comma</p>

        {/* Test helpers: bulk-add random entries to exercise large wheels */}
        <div className="entries__bulk">
          <span className="entries__bulk-label">Add random:</span>
          <button
            type="button"
            className="entries__bulk-btn"
            disabled={isSpinning}
            onClick={() => addRandomEntries(50)}
          >
            + 50
          </button>
          <button
            type="button"
            className="entries__bulk-btn"
            disabled={isSpinning}
            onClick={() => addRandomEntries(500)}
          >
            + 500
          </button>
        </div>
      </div>

      {/* Entry list */}
      <div className="entries-box __list">
        <ul>
          {entries.map((entry, index) => (
            <li
              key={entry.id}
              className={winner && winner.id === entry.id ? '--winner' : ''}
            >
              {entry.name}
              {!isSpinning && (
                <button
                  className="remove-entry"
                  onClick={() => handleRemove(index)}
                  aria-label="Remove entry"
                >
                  <svg width="14" height="16" viewBox="0 0 448 512" fill="currentColor">
                    <path d="M32 464a48 48 0 0 0 48 48h288a48 48 0 0 0 48-48V128H32zm272-256a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zm-96 0a16 16 0 0 1 32 0v224a16 16 0 0 1-32 0zM432 32H312l-9.4-18.7A24 24 0 0 0 281.1 0H166.8a23.72 23.72 0 0 0-21.4 13.3L136 32H16A16 16 0 0 0 0 48v32a16 16 0 0 0 16 16h416a16 16 0 0 0 16-16V48a16 16 0 0 0-16-16z" />
                  </svg>
                </button>
              )}
            </li>
          ))}
        </ul>
        {entries.length === 0 && <p>No entries yet. Add some above!</p>}
      </div>
    </div>
  );
};

export default SpinnerWheelEntries;
